/**
 * PremiumContext - Manages premium subscription status
 * 
 * DEV MODE PREMIUM OVERRIDE:
 * To enable premium features in development mode only (won't affect production):
 * 1. Create a .env file in the root directory (if it doesn't exist)
 * 2. Add: REACT_APP_DEV_PREMIUM=true
 * 3. Restart your dev server (npm start)
 * 
 * This will enable all premium features in development mode only.
 * In production builds, this flag is ignored and real premium status is used.
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db, verifySubscriptionStatus, syncSubscriptionStatus } from '../firebase';

const PremiumContext = createContext();

export const usePremium = () => {
  const context = useContext(PremiumContext);
  if (!context) {
    throw new Error('usePremium must be used within a PremiumProvider');
  }
  return context;
};

export const PremiumProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stripePromise, setStripePromise] = useState(null);
  const syncAttemptedRef = useRef(false);

  // Dev mode premium override - only works in development
  const isDevMode = process.env.NODE_ENV === 'development';
  const devPremiumEnabled = process.env.REACT_APP_DEV_PREMIUM === 'true';
  const shouldUseDevPremium = isDevMode && devPremiumEnabled;

  // Initialize Stripe
  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const { loadStripe } = await import('@stripe/stripe-js');
        const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        
        if (!publishableKey || publishableKey.includes('your_key') || publishableKey.includes('YOUR')) {
          setStripePromise(null);
          return;
        }
        
        const stripe = await loadStripe(publishableKey);
        setStripePromise(stripe);
      } catch (error) {
        console.error('Error initializing Stripe:', error);
        setStripePromise(null);
      }
    };
    initializeStripe();
  }, []);

  // Listen to premium status changes in real-time
  useEffect(() => {
    let unsubscribe;

    // If dev premium is enabled, skip Firebase checks and set premium to true
    if (shouldUseDevPremium) {
      console.log('🔧 DEV MODE: Premium enabled for development');
      setIsPremium(true);
      setLoading(false);
      return;
    }

    if (currentUser) {
      console.log('Setting up real-time listener for user:', currentUser.uid);
      
      // Set up real-time listener for user document
      unsubscribe = onSnapshot(
        doc(db, 'users', currentUser.uid),
        async (userDoc) => {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const premiumStatus = userData.isPremium === true;
            console.log('User document updated, premium status:', premiumStatus,
                '(raw:', userData.isPremium, ')');
            setIsPremium(premiumStatus);
            
            // If user has a stripeCustomerId but isPremium is false/undefined,
            // automatically sync from Stripe (only once per session)
            const hasCustomerId = userData.stripeCustomerId;
            const needsSync = hasCustomerId && 
                (userData.isPremium === false || userData.isPremium === undefined) &&
                !syncAttemptedRef.current;
            
            if (needsSync) {
              syncAttemptedRef.current = true;
              console.log('🔄 User has customerId but no premium status, syncing...');
              try {
                const syncResult = await syncSubscriptionStatus();
                console.log('✅ Sync result:', syncResult);
                if (syncResult?.isPremium) {
                  setIsPremium(true);
                }
              } catch (error) {
                console.error('❌ Error syncing subscription status:', error);
              }
            }
            
            // If isPremium is undefined and no customerId, set it to false explicitly
            if (userData.isPremium === undefined && !hasCustomerId) {
              console.log('⚠️ isPremium is undefined, setting to false');
              try {
                await setDoc(doc(db, 'users', currentUser.uid), {
                  isPremium: false,
                }, { merge: true });
              } catch (error) {
                console.error('Error setting isPremium to false:', error);
              }
            }
          } else {
            console.log('User document not found, creating...');
            // Create user document if it doesn't exist
            try {
              await setDoc(doc(db, 'users', currentUser.uid), {
                isPremium: false,
                createdAt: serverTimestamp(),
                email: currentUser.email
              });
              console.log('User document created');
              setIsPremium(false);
            } catch (error) {
              console.error('Error creating user document:', error);
              setIsPremium(false);
            }
          }
          setLoading(false);
        },
        (error) => {
          console.error('Error in real-time listener:', error);
          setIsPremium(false);
          setLoading(false);
        }
      );
    } else {
      console.log('No current user, setting premium to false');
      setIsPremium(false);
      setLoading(false);
      syncAttemptedRef.current = false;
    }

    // Cleanup listener on unmount or user change
    return () => {
      if (unsubscribe) {
        console.log('Cleaning up real-time listener');
        unsubscribe();
      }
      syncAttemptedRef.current = false;
    };
  }, [currentUser, shouldUseDevPremium]);

  // Update premium status
  const updatePremiumStatus = async (premiumStatus) => {
    if (currentUser) {
      try {
        console.log('Updating premium status in Firebase:', { userId: currentUser.uid, premiumStatus });
        await setDoc(doc(db, 'users', currentUser.uid), {
          isPremium: premiumStatus,
          premiumUpdatedAt: serverTimestamp()
        }, { merge: true });
        console.log('Premium status updated in Firebase successfully');
        setIsPremium(premiumStatus);
        console.log('Premium status updated in state:', premiumStatus);
      } catch (error) {
        console.error('Error updating premium status:', error);
        throw error;
      }
    } else {
      console.log('Cannot update premium status - no current user');
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = async (sessionId) => {
    console.log('handlePaymentSuccess called with:', {
      currentUser: !!currentUser,
      userId: currentUser?.uid,
      sessionId,
    });
    if (currentUser && sessionId) {
      try {
        console.log('Verifying subscription status from Stripe for user:',
            currentUser.uid);
        // Verify subscription status from Stripe (fallback if webhook hasn't fired)
        const result = await verifySubscriptionStatus(sessionId);
        console.log('Subscription verification result:', result);

        if (result && result.isPremium) {
          console.log('✅ Premium status verified from Stripe');
          // Wait a moment for Firestore to update
          await new Promise((resolve) => setTimeout(resolve, 500));
          // Force a refresh of the user document to pick up the change
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const premiumStatus = userData.isPremium === true;
            console.log('Current user document premium status:',
                premiumStatus, '(raw:', userData.isPremium, ')');
            setIsPremium(premiumStatus);
          } else {
            console.warn('⚠️ User document not found after verification');
          }
        } else {
          console.log('⚠️ Subscription not yet active or verification ' +
              'returned false');
          console.log('⚠️ Result:', result);
          // Try syncing from customer ID as fallback
          console.log('🔄 Trying to sync subscription status from customer ID...');
          try {
            const syncResult = await syncSubscriptionStatus();
            console.log('🔄 Sync result:', syncResult);
            if (syncResult && syncResult.isPremium) {
              console.log('✅ Premium status synced from customer ID');
              // Refresh user document
              const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                setIsPremium(userData.isPremium === true);
              }
            } else {
              // Final fallback: update locally
              console.log('🔄 Updating premium status locally as fallback...');
              await updatePremiumStatus(true);
              console.log('✅ Premium status updated locally');
            }
          } catch (syncError) {
            console.error('Error syncing subscription status:', syncError);
            // Final fallback: update locally
            await updatePremiumStatus(true);
            console.log('✅ Premium status updated locally as final fallback');
          }
        }
      } catch (error) {
        console.error('Error verifying subscription status:', error);
        console.error('Error details:', error.message, error.stack);
        // Try syncing from customer ID
        try {
          console.log('🔄 Trying sync as fallback...');
          const syncResult = await syncSubscriptionStatus();
          if (syncResult && syncResult.isPremium) {
            console.log('✅ Premium status synced from customer ID');
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              setIsPremium(userData.isPremium === true);
            }
          } else {
            await updatePremiumStatus(true);
            console.log('Premium status updated locally as fallback');
          }
        } catch (syncError) {
          console.error('Error syncing subscription status:', syncError);
          // Final fallback: update locally
          try {
            await updatePremiumStatus(true);
            console.log('Premium status updated locally as final fallback');
          } catch (updateError) {
            console.error('Error updating premium status:', updateError);
          }
        }
      }
    } else {
      console.log('Cannot update premium status - missing currentUser or ' +
          'sessionId');
    }
  };

  // Check if user can add more friends
  const canAddFriend = async () => {
    console.log('canAddFriend called:', { currentUser: !!currentUser, isPremium, shouldUseDevPremium });
    // Dev mode override
    if (shouldUseDevPremium) {
      console.log('🔧 DEV MODE: canAddFriend returning true');
      return true;
    }
    if (!currentUser) {
      console.log('No current user, returning false');
      return false;
    }
    if (isPremium) {
      console.log('User is premium, returning true');
      return true;
    }

    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const friendsQuery = query(
        collection(db, 'friends'),
        where('userId', '==', currentUser.uid)
      );
      const friendsSnapshot = await getDocs(friendsQuery);
      const friendCount = friendsSnapshot.docs.length;
      console.log('Friend count:', friendCount, 'Can add friend:', friendCount < 1);
      return friendCount < 1; // Free users can have 1 friend
    } catch (error) {
      console.error('Error checking friend limit:', error);
      return false;
    }
  };

  // Check if user can start journeys
  const canStartJourney = () => {
    // Dev mode override
    if (shouldUseDevPremium) {
      console.log('🔧 DEV MODE: canStartJourney returning true');
      return true;
    }
    return isPremium;
  };

  // Get friend count
  const getFriendCount = async () => {
    if (!currentUser) return 0;

    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const friendsQuery = query(
        collection(db, 'friends'),
        where('userId', '==', currentUser.uid)
      );
      const friendsSnapshot = await getDocs(friendsQuery);
      return friendsSnapshot.docs.length;
    } catch (error) {
      console.error('Error getting friend count:', error);
      return 0;
    }
  };

  // Check if a specific friend is accessible (first friend is always accessible)
  const isFriendAccessible = (friendId, friendsList) => {
    // Dev mode override
    if (shouldUseDevPremium) return true;
    if (isPremium) return true; // Premium users can access all friends
    
    if (!friendsList || friendsList.length === 0) return false;
    
    // Sort friends by creation date to get the first one
    const sortedFriends = [...friendsList].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime; // Oldest first
    });
    
    // First friend (oldest) is accessible
    return sortedFriends[0] && sortedFriends[0].id === friendId;
  };

  const value = {
    isPremium,
    loading,
    stripePromise,
    updatePremiumStatus,
    handlePaymentSuccess,
    canAddFriend,
    canStartJourney,
    getFriendCount,
    isFriendAccessible
  };

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
};
